import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { MessageCircle, ArrowLeft, Compass, BedDouble, MapPin, X } from "lucide-react";
import { api, WHATSAPP_LINK } from "../lib/config";
import { Reveal, SplitLines } from "../components/Motion";
import Seo from "../components/Seo";

export default function PrivateTripDetail() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  useEffect(() => { api.get(`/trips/${id}`).then((r) => setTrip(r.data)).catch(() => setTrip(false)); }, [id]);

  if (trip === null) return <div className="pt-32 text-center font-mono text-sm text-owt-muted">Loading...</div>;
  if (trip === false) return <div className="pt-32 text-center"><Link to="/private-trips" className="underline">Trip not found — back to private trips</Link></div>;

  const waMsg = `Enquire for Private Trip — ${trip.name}. Please share full details.`;

  return (
    <div className="pt-24 md:pt-28" data-testid={`private-trip-detail-${trip.id}`}>
      <Seo
        title={trip.name}
        description={trip.short_description || trip.description?.slice(0, 160)}
        path={`/private-trips/${trip.id}`}
        image={trip.hero_image}
        type="article"
      />

      {/* HERO */}
      <section ref={heroRef} className="relative h-[80svh] min-h-[520px] overflow-hidden">
        <motion.img style={{ y: heroY, scale: 1.05 }} src={trip.hero_image} alt={trip.name} className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-owt-ink/85 via-owt-ink/20 to-transparent"/>
        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-12 md:pb-16 text-owt-surface">
          <Link to="/private-trips" className="font-mono text-[11px] tracking-[0.25em] uppercase inline-flex items-center gap-2 mb-6 hover:text-owt-terracotta" data-testid="back-private-trips"><ArrowLeft className="w-4 h-4"/> All Private Trips</Link>
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase opacity-80 flex items-center gap-3">
            <span className="stamp bg-transparent !border-owt-surface !text-owt-surface">Private</span>
            {trip.country} · {trip.duration_days} Days · Designed around you
          </div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9] tracking-tighter mt-4">
            <SplitLines text={trip.name}/>
          </h1>
          <p className="max-w-2xl mt-4 opacity-90 text-lg">{trip.short_description}</p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">

          {/* Sticky WhatsApp panel */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start" data-testid="private-sticky">
            <div className="boarding-pass p-6 md:p-8">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted mb-4">Private · Custom · V.I.P</div>
              <h3 className="font-display text-3xl text-owt-forest leading-tight">Curated<br/>around you.</h3>
              <p className="mt-3 text-owt-muted text-sm leading-relaxed">
                Pricing, dates and group size are all customised. WhatsApp us and we'll share the full details — inclusions, stays, options and everything in between.
              </p>
              <div className="dashed-line my-5"/>
              <a
                href={WHATSAPP_LINK(waMsg)}
                target="_blank" rel="noreferrer"
                data-testid="private-whatsapp"
                className="w-full bg-[#25D366] text-white py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4"/> WhatsApp Us
              </a>
              <div className="mt-3 font-mono text-[9px] tracking-[0.2em] uppercase text-owt-muted text-center">Full details · zero commitment</div>
            </div>
          </aside>

          {/* Main content */}
          <div className="lg:col-span-8 space-y-14">
            <Reveal>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-3">About the trip</div>
              <p className="font-display text-2xl md:text-3xl text-owt-forest leading-tight">{trip.description}</p>
            </Reveal>

            {/* Day-by-day (Thrillophilia-style) */}
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-terracotta">Day by day</div>
                <div className="flex-1 border-t border-dashed border-owt-border"/>
                <Compass className="w-4 h-4 text-owt-terracotta compass-spin" strokeWidth={1.2}/>
              </div>
              <div className="mb-6 border-l-2 border-owt-terracotta pl-4 py-2 bg-owt-sand/40">
                <p className="text-sm text-owt-ink/80 leading-relaxed">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-terracotta mr-2">Please note</span>
                  Itinerary is indicative — everything (pace, stays, days) is customised when you enquire.
                </p>
              </div>

              <Accordion type="multiple" className="border-t border-owt-border" defaultValue={["d1"]}>
                {trip.itinerary.map((d) => (
                  <AccordionItem key={d.day} value={`d${d.day}`} className="border-b border-owt-border">
                    <AccordionTrigger className="font-display text-2xl text-owt-forest hover:no-underline py-5" data-testid={`private-day-${d.day}`}>
                      <span className="flex items-baseline gap-4 text-left">
                        <span className="font-mono text-sm text-owt-terracotta">Day {String(d.day).padStart(2, "0")}</span>
                        <span>{d.title}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8">
                      <div className="grid md:grid-cols-12 gap-6">
                        <div className="md:col-span-7">
                          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-terracotta mb-2 flex items-center gap-2">
                            <MapPin className="w-3 h-3"/> Activities
                          </div>
                          <p className="text-owt-ink/85 leading-relaxed">{d.description}</p>
                          {Array.isArray(d.images) && d.images.length > 0 && (
                            <div className="grid grid-cols-3 gap-2 mt-4">
                              {d.images.map((src, i) => (
                                <div key={i} className="aspect-square overflow-hidden bg-owt-sand">
                                  <motion.img src={src} alt={`Day ${d.day} activity`} className="w-full h-full object-cover" whileHover={{ scale: 1.05 }} transition={{ duration: 1 }}/>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="md:col-span-5">
                          <div className="border border-dashed border-owt-border bg-owt-surface p-4 h-full">
                            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted mb-2 flex items-center gap-2">
                              <BedDouble className="w-3 h-3 text-owt-forest"/> Stay
                            </div>
                            {d.hotel_name ? (
                              <div className="font-display text-lg text-owt-forest leading-tight">{d.hotel_name}</div>
                            ) : (
                              <div className="font-display text-lg text-owt-muted italic leading-tight">Handpicked stay — details shared on enquiry</div>
                            )}
                            {Array.isArray(d.hotel_images) && d.hotel_images.length > 0 ? (
                              <div className="grid grid-cols-2 gap-1.5 mt-3">
                                {d.hotel_images.slice(0, 4).map((src, i) => (
                                  <div key={i} className="aspect-[4/3] overflow-hidden bg-owt-sand">
                                    <img src={src} alt={`${d.hotel_name || "Stay"} photo ${i+1}`} className="w-full h-full object-cover"/>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="mt-3 aspect-[4/3] border border-dashed border-owt-border flex items-center justify-center">
                                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-owt-muted">Photos on enquiry</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>

            {/* What we exclude - one small note */}
            {trip.exclusions?.length > 0 && (
              <Reveal>
                <div className="border border-owt-border p-6 bg-owt-sand/40">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-terracotta mb-3">A note on what's excluded</div>
                  <ul className="space-y-1">
                    {trip.exclusions.map((i, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-owt-muted"><X className="w-3.5 h-3.5 mt-1 text-owt-terracotta shrink-0"/> <span>{i}</span></li>
                    ))}
                  </ul>
                  <p className="mt-3 text-sm text-owt-ink/70">Everything else is designed around you — WhatsApp us for the full picture.</p>
                </div>
              </Reveal>
            )}

            {/* CTA */}
            <Reveal>
              <div className="border border-owt-border p-8 md:p-12 bg-owt-forest text-owt-surface flex flex-col md:flex-row md:items-center gap-6 justify-between">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-sand/70 mb-3">Ready to start?</div>
                  <h3 className="font-display text-3xl md:text-4xl leading-tight">Message us on WhatsApp<br/>and we'll design your trip.</h3>
                </div>
                <a
                  href={WHATSAPP_LINK(waMsg)}
                  target="_blank" rel="noreferrer"
                  data-testid="cta-private-whatsapp"
                  className="shrink-0 bg-[#25D366] text-white px-6 py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:opacity-90 inline-flex items-center gap-2 w-fit"
                >
                  <MessageCircle className="w-4 h-4"/> WhatsApp Us
                </a>
              </div>
            </Reveal>

            {trip.faqs?.length > 0 && (
              <Reveal>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-4">FAQ</div>
                <Accordion type="single" collapsible className="border-t border-owt-border">
                  {trip.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`f${i}`} className="border-b border-owt-border">
                      <AccordionTrigger className="font-display text-xl text-owt-forest hover:no-underline">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-owt-muted">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
